import styled, { x, css } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { IMAGE_MAP2 } from '../itemIds';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { select } from '~/lib/select';
import {
  useEditorStore,
  EditorMediaFile,
  SequenceItem,
  EditorChapter
} from '~/shell/Store';
import { EditBg } from '~/editor/buttons/EditBg';
import AddTextAudioModal from '~/editor/addTextAudioModal/AddTextAudioModal';
import AudioPoint from '~/editor/audioPoint/AudioPoint';
import { AUDIO_POPUP, VIDEO_POPUP } from '../itemIds';
import {
  nudgeDraggedHotspotAwayFromFixed,
  usableHotspotImageSize
} from '~/training/sequence/templates/translators/interactive4Overlap';
import { parseAudioPointId } from './audioPointIdUtils';

interface AudioPointData {
  id: string;
  text: string;
  body?: string;
  iconType?: string | null;
  iconValue?: number | null;
  audio: { id: number; mediaUrl: string; originalFilename?: string } | null;
  coordinates: { x: number; y: number };
}

interface Props {
  imageUrl: string;
  body?: string;
  isFullScreen?: boolean;
  w?: string;
  h?: string;
  color?: string;
  enableAudioPoints?: boolean; // allow adding/moving audio/text points
  showBackgroundImage?: boolean;
  showColorPicker?: boolean;
  parentId?: string;
}

export type ImageMap2Definition = ItemDefinition<typeof IMAGE_MAP2, Props>;

const Instructions = styled.div`
  position: fixed;
  left: 50%;
  top: 39px;
  transform: translate(-50%, -50%);
  margin: 0 auto;
  background-color: white;
  padding: 7px 25px;
  border-radius: 8px;
  opacity: 0.8;
  font-family: 'Plus Jakarta Sans';
`;

const StyledImage = styled.img<{ maxHeight: string; w: string; h: string }>`
  width: ${p => p.w};
  height: ${p => p.h};
  max-height: ${p => p.maxHeight};
`;

const BackgroundImage = styled.div<{ imageUrl: string; color?: string }>`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  ${p =>
    p.color
      ? css`
          background-color: ${p.color};
        `
      : css`
          background-image: url(${p.imageUrl});
          background-size: cover;
          filter: blur(10px);
        `}
`;

const Container = styled(x.div)<{ $noScroll?: boolean; noBorder?: boolean }>`
  overflow: ${p => (p.$noScroll ? 'hidden' : 'auto')};
  display: flex;
  align-items: center;
  border-radius: ${p => (p.noBorder ? '0px' : '20px')};
  box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  position: relative;

  .edit-bg-btn {
    margin-right: 16px;
  }
`;

const sequenceSelector = (s: SequenceStore) => select(s, 'blockers');

export class ImageMap2 extends Item<typeof IMAGE_MAP2, Props> {
  protected render({
    imageUrl,
    isFullScreen = false,
    body,
    children,
    w = '100%',
    h = '100%',
    color = '',
    enableAudioPoints = true,
    showBackgroundImage = true,
    showColorPicker = false,
    parentId,
    ...props
  }: RenderProps<Props>) {
    const [bgColor, setBgColor] = useState(color);
    const [colorForSequence, setColorForSequence] = useState(color);
    const { blockers } = useSequenceStore(sequenceSelector);
    const ref = useRef<HTMLDivElement | null>(null);
    const [width, setWidth] = useState('100vw');
    const [height, setHeight] = useState(h);
    const {
      contentEditable,
      addSubItem,
      sequence,
      apiService,
      setSequence,
      updateSequence,
      setForceUpdateSequence,
      training,
      updateFile,
      setTraining
    } = useEditorStore();

    const divElRef = useRef<HTMLDivElement>(null);
    const [showAddTextAudioModal, setShowAddTextAudioModal] = useState(false);
    const [clickPosition, setClickPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    const [editingAudioPoint, setEditingAudioPoint] =
      useState<AudioPointData | null>(null);

    const [movedAudioPoints, setMovedAudioPoints] = useState<
      Record<string, { x: number; y: number }>
    >({});

    const [tempAudioPoints, setTempAudioPoints] = useState<SequenceItem[]>([]);
    const [isCreatingAudioPoint, setIsCreatingAudioPoint] = useState(false);

    const [creatingPosition, setCreatingPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    const [isEditingAudioPoint, setIsEditingAudioPoint] = useState(false);
    const [editingPosition, setEditingPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    const [isMovingAudioPoint, setIsMovingAudioPoint] = useState(false);
    const [movingPosition, setMovingPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    const [mousePosition, setMousePosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    const handleResize = () => {
      if (ref?.current?.clientWidth === window.innerWidth) {
        setHeight('auto');
        setWidth('100vw');
      }
      if (ref?.current?.clientHeight === window.innerHeight) {
        setHeight('100vh');
        setWidth('auto');
      }
      if (ref!.current!.clientWidth > window.innerWidth) {
        setHeight('auto');
        setWidth('100vw');
      }
      if (ref!.current!.clientHeight > window.innerHeight) {
        setHeight('100vh');
        setWidth('auto');
      }
    };

    const handleImageLoad = () => {
      if (isFullScreen) {
        handleResize();
      }
    };

    const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
      // Only handle clicks in editor mode & when audio points enabled
      if (!contentEditable || !enableAudioPoints) return;

      // Prevent default behavior
      event.preventDefault();
      event.stopPropagation();

      // Get the image element and its bounding box
      const imageElement = event.currentTarget;
      const rect = imageElement.getBoundingClientRect();

      // Calculate click position relative to the image as percentages
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      // Store the click position and show the add text and audio modal
      setClickPosition({
        x: Math.round(x * 100) / 100,
        y: Math.round(y * 100) / 100
      });

      setShowAddTextAudioModal(true);
    };

    const handleImageMouseMove = (
      event: React.MouseEvent<HTMLImageElement>
    ) => {
      // Only track mouse in editor mode & when enabled
      if (!contentEditable || !enableAudioPoints) {
        setMousePosition(null);
        return;
      }

      // Update mouse position for tooltip
      setMousePosition({
        x: event.clientX,
        y: event.clientY
      });
    };

    const handleImageMouseLeave = () => {
      // Hide tooltip when mouse leaves image or feature disabled
      setMousePosition(null);
    };

    const updateBackgroundImage = ({
      id,
      file,
      type
    }: {
      id: string;
      file: unknown;
      type: string;
    }) => {
      const imageMapItem = sequence!.items?.find(
        (item: SequenceItem) => item.id === props.id
      );

      const backgroundItem = sequence!.items?.find(
        (item: SequenceItem) => item.__typename === 'Background'
      );

      // Mirror Interactive3 translator: per-field item ?? background,
      // then usable check, then 1024.
      const prevSize = usableHotspotImageSize(
        Number(
          imageMapItem?.pictures?.[0]?.width ??
            backgroundItem?.pictures?.[0]?.width
        ),
        Number(
          imageMapItem?.pictures?.[0]?.height ??
            backgroundItem?.pictures?.[0]?.height
        )
      );
      const prevWidth = prevSize?.width ?? 1024;
      const prevHeight = prevSize?.height ?? 1024;

      const newSize = usableHotspotImageSize(
        (file as EditorMediaFile)?.width as number,
        (file as EditorMediaFile)?.height as number
      );
      const newWidth = newSize?.width ?? 1024;
      const newHeight = newSize?.height ?? 1024;

      const subitems = imageMapItem?.items || [];
      const updates: Array<{ id: string; coords: string }> = [];

      subitems.forEach((subitem: SequenceItem, index: number) => {
        const subitemId = String(subitem.id ?? `${props.id}-${index}`);
        const _id = subitemId.split('-')[0];

        const movedPosition =
          movedAudioPoints[subitemId] || movedAudioPoints[_id];

        let percentX: number;
        let percentY: number;

        if (movedPosition?.x != null && movedPosition?.y != null) {
          percentX = movedPosition.x;
          percentY = movedPosition.y;
        } else {
          const parts = (subitem.coordinates || '').split(',');
          const oldPxX = parseFloat(parts[0]) || 0;
          const oldPxY = parseFloat(parts[1]) || 0;
          percentX = prevWidth > 0 ? (oldPxX * 100) / prevWidth : 0;
          percentY = prevHeight > 0 ? (oldPxY * 100) / prevHeight : 0;
        }

        const newPxX = (percentX / 100) * newWidth;
        const newPxY = (percentY / 100) * newHeight;

        updates.push({ id: _id, coords: `${newPxX},${newPxY}` });
      });

      if (updates.length > 0) {
        updates.forEach(u => {
          updateSequence(u.id, 'settings', { coordinates: u.coords });
          updateSequence(u.id, 'coordinates', u.coords);
        });
      }

      updateFile(id, file as EditorMediaFile, type);
    };

    const handleTextAudioSubmit = (data: {
      text: string;
      body?: string;
      audio: { id: number; mediaUrl: string; originalFilename?: string } | null;
      coordinates: { x: number; y: number };
      iconType: 'icon' | 'number';
      iconValue: number | null;
    }) => {
      try {
        if (!sequence?.id) {
          console.error('Sequence not available');
          return;
        }

        // Check if we're editing an existing audio point
        if (editingAudioPoint) {
          // Parse the audio point ID using the utility function
          const idResult = parseAudioPointId(editingAudioPoint.id);

          // Only handle real backend IDs
          if (
            idResult.isTemporary ||
            !idResult.isValid ||
            idResult.numericId === null
          ) {
            console.error(
              'Invalid audio point ID for editing:',
              idResult.originalId
            );
            setShowAddTextAudioModal(false);
            setEditingAudioPoint(null);
            setClickPosition(null);
            return;
          }

          const numericId = idResult.numericId;

          // Close modal immediately for better UX
          setShowAddTextAudioModal(false);

          // Show loader at the editing position (use the actual audio point coordinates)
          setIsEditingAudioPoint(true);
          setEditingPosition({
            x: editingAudioPoint?.coordinates.x || 0,
            y: editingAudioPoint?.coordinates.y || 0
          });

          setEditingAudioPoint(null);
          setClickPosition(null);

          // Update backend and force refresh (similar to updateFile pattern)
          if (apiService) {
            (async () => {
              try {
                // Update title and body
                await apiService.updateSubItem(numericId, 'title', data.text);
                await apiService.updateSubItem(
                  numericId,
                  'body',
                  data.body || ''
                );

                await apiService.updateSubItemBatch(numericId, {
                  icon_type: data.iconType,
                  ...(data.iconType === 'number' && data.iconValue != null
                    ? { icon_value: data.iconValue }
                    : {})
                });

                // Handle audio update - can be empty, new, or existing
                if (data.audio?.id && data.audio.id !== 0) {
                  // Use updateFile (same method as creation) instead of updateSubItem
                  const { updateFile } = useEditorStore.getState();
                  updateFile(
                    numericId.toString(),
                    { id: data.audio.id, mediaUrl: data.audio.mediaUrl },
                    'audios'
                  );
                } else {
                  // No audio provided - check if we need to remove existing audio or just update text
                  const currentItem = sequence.items
                    ?.flatMap((item: SequenceItem) => item.items || [])
                    .find((item: SequenceItem) => item.id == numericId);

                  const hadAudioBefore =
                    (currentItem?.audios?.length as number) > 0;

                  if (!hadAudioBefore) {
                    // Text-only popup - just updating text (already done above)
                    // No need to delete and recreate
                  } else {
                    // Remove audio: clear audio_ids on the existing point (don't delete+recreate)
                    await apiService.updateSubItem(numericId, 'audio_ids', []);
                  }
                }

                // Force refresh from backend
                const freshSequenceData = await apiService.getSequenceInfo(
                  sequence.id
                );

                if (freshSequenceData?.sequence) {
                  // Update sequence with fresh data
                  setSequence(freshSequenceData.sequence);

                  // Also update training to keep it in sync (following Header.tsx pattern)
                  if (training) {
                    const updatedTraining = {
                      ...training,
                      chapters: training.chapters.map(
                        (chapter: EditorChapter) => ({
                          ...chapter,
                          chapterSequences: chapter?.chapterSequences?.map(
                            (
                              chapterSequence: EditorChapter['chapterSequences'][number]
                            ) => {
                              return chapterSequence.sequence.id === sequence.id
                                ? {
                                    ...chapterSequence,
                                    sequence: {
                                      ...chapterSequence.sequence,
                                      ...freshSequenceData.sequence
                                    }
                                  }
                                : chapterSequence;
                            }
                          )
                        })
                      )
                    };
                    setTraining(updatedTraining);
                  }

                  // CRITICAL: Set force update flag to trigger re-render
                  setForceUpdateSequence(true);

                  // Hide editing loader after successful update
                  setIsEditingAudioPoint(false);
                  setEditingPosition(null);
                } else {
                  // Hide loader if no fresh data received
                  setIsEditingAudioPoint(false);
                  setEditingPosition(null);
                }
              } catch (error) {
                // On error, still try to refresh to restore correct state
                try {
                  const freshSequenceData = await apiService.getSequenceInfo(
                    sequence.id
                  );
                  if (freshSequenceData?.sequence) {
                    setSequence(freshSequenceData.sequence);

                    // Also update training on error recovery
                    if (training) {
                      const updatedTraining = {
                        ...training,
                        chapters: training.chapters.map(
                          (chapter: EditorChapter) => ({
                            ...chapter,
                            chapterSequences: chapter?.chapterSequences?.map(
                              (
                                chapterSequence: EditorChapter['chapterSequences'][number]
                              ) => {
                                return chapterSequence.sequence.id ===
                                  sequence.id
                                  ? {
                                      ...chapterSequence,
                                      sequence: {
                                        ...chapterSequence.sequence,
                                        ...freshSequenceData.sequence
                                      }
                                    }
                                  : chapterSequence;
                              }
                            )
                          })
                        )
                      };
                      setTraining(updatedTraining);
                    }

                    // Force update on error recovery too
                    setForceUpdateSequence(true);

                    // Hide editing loader after error recovery
                    setIsEditingAudioPoint(false);
                    setEditingPosition(null);
                  }
                } catch (refreshError) {
                  // Hide loader even if refresh fails
                  setIsEditingAudioPoint(false);
                  setEditingPosition(null);
                }
              }
            })();
          }

          return;
        }

        if (!addSubItem) {
          console.error('addSubItem not available');
          return;
        }

        // Create new audio popup item structure similar to Interactive3 translator
        const newAudioPopupItem = {
          type: AUDIO_POPUP,
          items: [],
          values: {
            left: data.coordinates.x,
            top: data.coordinates.y,
            name: data.audio?.originalFilename || 'Audio Point',
            title: data.text,
            body: data.body || '',
            mediaUrl: data.audio?.mediaUrl || '',
            subtitlesUrl: '', // Could be added later if needed
            blocking: false,
            replay: true,
            isPopupCentered: false,
            scroll: true,
            pointNumber:
              React.Children.toArray(children).filter(
                (child: React.ReactElement) => {
                  if (child?.props?.type === AUDIO_POPUP) {
                    const left = child.props.left;
                    const top = child.props.top;
                    return (
                      !isNaN(left) &&
                      !isNaN(top) &&
                      !(left === 0 && top === 0) &&
                      left != null &&
                      top != null
                    );
                  }
                  return false;
                }
              ).length + 1 // Number for the audio point
          }
        };

        // Create the sub-item data for the API
        // Convert percentage coordinates to absolute pixels for backend
        // Get actual image dimensions from sequence data to match translator
        // Find current ImageMap item (this component's parent) to get real dimensions
        const currentImageMapItem = sequence.items?.find(
          (item: SequenceItem) => item.id === props.id
        );

        const createBackgroundItem = sequence.items?.find(
          (item: SequenceItem) =>
            item.__typename === 'Background' || item.type === 'BACKGROUND'
        );

        // Mirror Interactive3 translator: per-field item ?? background,
        // then usable check (1×1 solids rejected), then 1024.
        const currentSize = usableHotspotImageSize(
          Number(
            currentImageMapItem?.pictures?.[0]?.width ??
              createBackgroundItem?.pictures?.[0]?.width
          ),
          Number(
            currentImageMapItem?.pictures?.[0]?.height ??
              createBackgroundItem?.pictures?.[0]?.height
          )
        );
        const IMAGE_WIDTH = currentSize?.width ?? 1024;
        const IMAGE_HEIGHT = currentSize?.height ?? 1024;

        const absoluteX = (data.coordinates.x * IMAGE_WIDTH) / 100;
        const absoluteY = (data.coordinates.y * IMAGE_HEIGHT) / 100;

        // Count existing audio points from children for position
        const audioChildrenCount = React.Children.toArray(children).filter(
          (child: React.ReactElement) => child?.props?.type === AUDIO_POPUP
        ).length;

        const subItemData = {
          sequenceId: sequence.id,
          data: {
            parent_id: props.id,
            position: audioChildrenCount + 1,
            type: 'Popup', // Back to Popup as that's what works
            coordinates: `${Math.round(absoluteX)},${Math.round(absoluteY)}`,
            title: data.text,
            body: data.body || '',
            // Send just the audio ID as backend suggested
            audio_ids: data.audio ? [data.audio.id] : [],
            icon_type: data.iconType,
            ...(data.iconType === 'number' && data.iconValue != null
              ? { icon_value: data.iconValue }
              : {})
          },
          newItem: newAudioPopupItem
        };

        // Show loader at the clicked position
        setIsCreatingAudioPoint(true);
        setCreatingPosition(data.coordinates);

        // Close modal for better UX
        setShowAddTextAudioModal(false);
        setClickPosition(null);

        // Create item in backend (non-blocking)
        if (apiService) {
          apiService
            .addSubItem(subItemData.sequenceId, subItemData.data)
            .then(newItem => {
              if (newItem && newItem.subitem && newItem.subitem.id) {
                const realId = newItem.subitem.id;

                const coordsPromise = apiService.updateSubItem(
                  realId,
                  'coordinates',
                  `${Math.round(absoluteX)},${Math.round(absoluteY)}`
                );

                // Add to temporary state immediately for ALL audio points (with or without audio)
                const tempAudioPoint = {
                  id: realId,
                  type: AUDIO_POPUP,
                  left: data.coordinates.x,
                  top: data.coordinates.y,
                  values: {
                    title: data.text,
                    body: data.body || '',
                    mediaUrl: data.audio?.mediaUrl || '',
                    name: data.audio?.originalFilename || 'Audio Point',
                    // Carried over so the marker is drawn as chosen right away
                    // instead of falling back to '+' until the backend answers.
                    iconType: data.iconType,
                    iconValue: data.iconValue
                  }
                };

                setTempAudioPoints(prev => [...prev, tempAudioPoint]);

                // Update audio if provided (in parallel, non-blocking)
                if (data.audio) {
                  const { updateFile } = useEditorStore.getState();
                  updateFile(
                    realId,
                    { id: data.audio.id, mediaUrl: data.audio.mediaUrl },
                    'audios'
                  );
                }

                // Wait for coordinates to be saved, then ensure backend has the point
                coordsPromise
                  .then(() => {
                    // For text-only points, do a quick refresh to ensure they appear from backend
                    if (!data.audio && apiService && sequence?.id) {
                      setTimeout(() => {
                        apiService
                          .getSequenceInfo(sequence.id)
                          .then(freshSequence => {
                            if (freshSequence?.sequence) {
                              setSequence(freshSequence.sequence);
                              // Clear temp point after confirming backend has it
                              setTimeout(() => {
                                setTempAudioPoints(prev =>
                                  prev.filter(p => p.id !== realId)
                                );
                              }, 500);
                            }
                          });
                      }, 1000);
                    } else {
                      // For audio points, just clear temp after delay (they use more complex backend processing)
                      setTimeout(() => {
                        setTempAudioPoints(prev => {
                          const filtered = prev.filter(p => p.id !== realId);
                          return filtered;
                        });
                      }, 3000);
                    }

                    // Hide loader after coordinates are saved
                    setIsCreatingAudioPoint(false);
                    setCreatingPosition(null);
                  })
                  .catch(error => {
                    console.error('❌ Error updating coordinates:', error);
                    // Remove temp point on error and hide loader
                    setTempAudioPoints(prev =>
                      prev.filter(p => p.id !== realId)
                    );
                    setIsCreatingAudioPoint(false);
                    setCreatingPosition(null);
                  });
              }
            })
            .catch(error => {
              console.error('❌ Backend error:', error);
            });
        } else {
          console.error('❌ apiService not available');
        }
      } catch (error) {
        console.error('❌ Error in handleTextAudioSubmit:', error);
      }
    };

    const handleAddTextAudioModalClose = () => {
      setShowAddTextAudioModal(false);
      setClickPosition(null);
      setEditingAudioPoint(null);
    };

    const handleEditAudioPoint = (audioPointData: AudioPointData) => {
      setEditingAudioPoint(audioPointData);
      setClickPosition({
        x: audioPointData.coordinates.x,
        y: audioPointData.coordinates.y
      });
      setShowAddTextAudioModal(true);
    };

    const handleDeleteAudioPoint = (audioPointId: string) => {
      if (!contentEditable || !sequence) return;

      // Only handle real backend IDs through the popup menu
      // The actual deletion is handled through AudioPointPopupMenu -> removeSubItem
    };

    const handleMoveAudioPoint = (
      audioPointId: string,
      newCoordinates: { x: number; y: number }
    ) => {
      if (
        !contentEditable ||
        !updateSequence ||
        !sequence ||
        !enableAudioPoints
      )
        return;

      // Parse the audio point ID using the utility function
      const idResult = parseAudioPointId(audioPointId);

      const _ids = String(audioPointId).split('-');
      idResult.numericId = parseInt(_ids[0], 10);

      // Only handle real backend IDs
      if (
        idResult.isTemporary ||
        !idResult.isValid ||
        idResult.numericId === null ||
        Number.isNaN(idResult.numericId)
      ) {
        console.error(
          'Invalid audio point ID for moving:',
          idResult.originalId
        );
        return;
      }

      const numericId = idResult.numericId;

      const imageMapItem = sequence.items?.find(
        (item: SequenceItem) => item.id === props.id
      );
      const backgroundItem = sequence.items?.find(
        (item: SequenceItem) =>
          item.__typename === 'Background' || item.type === 'BACKGROUND'
      );

      // Mirror Interactive3 translator: per-field item ?? background, then
      // usable check, then 1024 — never rescue a rejected 1×1 solid with
      // another source or the loaded <img> natural size.
      const moveBasisSize = usableHotspotImageSize(
        Number(
          imageMapItem?.pictures?.[0]?.width ??
            backgroundItem?.pictures?.[0]?.width
        ),
        Number(
          imageMapItem?.pictures?.[0]?.height ??
            backgroundItem?.pictures?.[0]?.height
        )
      );

      const IMAGE_WIDTH = moveBasisSize?.width ?? 1024;
      const IMAGE_HEIGHT = moveBasisSize?.height ?? 1024;

      const fixedOthers: { left: number; top: number }[] = [];
      React.Children.forEach(children as React.ReactNode, child => {
        if (!React.isValidElement(child)) return;
        if (
          child.props?.type !== AUDIO_POPUP &&
          child.props?.type !== VIDEO_POPUP
        ) {
          return;
        }
        const fullId = String(child.props.id);
        if (fullId === audioPointId) return;
        const mv =
          movedAudioPoints[fullId] ?? movedAudioPoints[fullId.split('-')[0]];
        fixedOthers.push({
          left: mv?.x ?? child.props.left,
          top: mv?.y ?? child.props.top
        });
      });
      tempAudioPoints.forEach(t => {
        fixedOthers.push({
          left: t.left as number,
          top: t.top as number
        });
      });

      const nudged = nudgeDraggedHotspotAwayFromFixed(
        { left: newCoordinates.x, top: newCoordinates.y },
        fixedOthers,
        isMobile,
        IMAGE_WIDTH,
        IMAGE_HEIGHT
      );
      const adjustedCoordinates = {
        x: nudged.left,
        y: nudged.top
      };

      // Convert to absolute coordinates for backend using actual dimensions
      const absoluteX = (adjustedCoordinates.x * IMAGE_WIDTH) / 100;
      const absoluteY = (adjustedCoordinates.y * IMAGE_HEIGHT) / 100;
      const coordinatesString = `${Math.round(absoluteX)},${Math.round(
        absoluteY
      )}`;

      // Show moving loader
      setIsMovingAudioPoint(true);
      setMovingPosition(adjustedCoordinates);

      // Store the moved coordinates for immediate UI update
      setMovedAudioPoints(prev => ({
        ...prev,
        [audioPointId]: adjustedCoordinates
      }));

      // Use updateSequence to update backend
      updateSequence(numericId, 'coordinates', coordinatesString);

      // Hide loader after backend update (let optimistic rendering handle the UI)
      setTimeout(() => {
        setIsMovingAudioPoint(false);
        setMovingPosition(null);

        // DO NOT automatically clear moved coordinates - they will persist until manually refreshed
        // This ensures the audio point stays in the moved position regardless of backend delays
      }, 800);
    };

    if (isFullScreen) {
      useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
        };
      }, []);
    }

    const updateColor = (color: string) => {
      if (!parentId) return;
      setBgColor(color);
      updateSequence(parentId, 'color', color);
      setColorForSequence(color);
    };

    return (
      <Container
        w={w}
        h={h}
        $noScroll={contentEditable && isFullScreen}
        noBorder={!!colorForSequence}
      >
        <BackgroundImage
          imageUrl={showBackgroundImage ? imageUrl : ''}
          color={colorForSequence}
        />
        <x.div w={'100%'} display={'flex'} justifyContent={'center'}>
          <x.div position={'relative'} ref={ref} data-image-container="true">
            <StyledImage
              width="100%"
              height="100%"
              maxHeight={h}
              alt=""
              src={imageUrl}
              w={isFullScreen ? width : '100%'}
              h={isFullScreen ? height : '100%'}
              onLoad={handleImageLoad}
              onClick={handleImageClick}
              onMouseMove={handleImageMouseMove}
              onMouseLeave={handleImageMouseLeave}
              style={{
                cursor:
                  contentEditable && enableAudioPoints ? 'pointer' : 'default'
              }}
            />
            {/* Render children: in editor skip AUDIO_POPUP (we render AudioPoint below); in learner render all */}
            {React.Children.map(children, (child: React.ReactElement) => {
              if (contentEditable) {
                if (child?.props?.type === AUDIO_POPUP) return null;
                return child;
              }
              return child;
            })}

            {/* Editor-only: render AudioPoint components for valid AUDIO_POPUP items */}
            {contentEditable &&
              (() => {
                let audioPointNumber = 0;
                return React.Children.map(
                  children,
                  (child: React.ReactElement) => {
                    if (child?.props?.type === AUDIO_POPUP) {
                      const left = child.props.left;
                      const top = child.props.top;
                      const values = child.props.values || child.props;

                      // Skip if coordinates are invalid
                      if (
                        isNaN(left) ||
                        isNaN(top) ||
                        left == null ||
                        top == null
                      ) {
                        return null;
                      }

                      // Increment number only for valid audio points
                      audioPointNumber++;

                      // Only use real backend IDs - skip if no real ID
                      const audioPointId = child.props.id;
                      if (!audioPointId) {
                        return null; // Skip audio points without real backend IDs
                      }
                      const movedCoords =
                        movedAudioPoints[String(audioPointId)] ??
                        movedAudioPoints[String(audioPointId).split('-')[0]];

                      // Extract audio ID from mediaUrl (e.g., "77531__en_1755702919.mp3" -> 77531)
                      const extractAudioIdFromUrl = (url: string): number => {
                        try {
                          const filename = url.split('/').pop() || '';
                          const match = filename.match(/^(\d+)__/);
                          return match ? parseInt(match[1], 10) : 0;
                        } catch {
                          return 0;
                        }
                      };

                      // Extract audio name from mediaUrl (remove ID prefix and timestamp)
                      const extractAudioNameFromUrl = (url: string): string => {
                        try {
                          const filename = url.split('/').pop() || '';
                          // Remove the ID prefix (77531__) and timestamp (?1755702919)
                          const nameWithoutId = filename
                            .replace(/^\d+__/, '')
                            .split('?')[0];
                          return nameWithoutId || 'Selected Audio';
                        } catch {
                          return 'Selected Audio';
                        }
                      };

                      const audioPointData = {
                        id: audioPointId,
                        // title is the hotspot marker; content title lives in name
                        text: values.name ?? values.body ?? '',
                        body: values.body || '',
                        iconType: values.iconType ?? null,
                        iconValue:
                          values.iconValue != null
                            ? Number(values.iconValue)
                            : null,
                        audio: values.mediaUrl
                          ? {
                              // Prefer the real backend audio id; only fall back
                              // to parsing the URL (legacy) when it's missing, so
                              // editing title/body doesn't drop the audio.
                              id:
                                Number(values.audioId) ||
                                extractAudioIdFromUrl(values.mediaUrl),
                              mediaUrl: values.mediaUrl,
                              originalFilename: extractAudioNameFromUrl(
                                values.mediaUrl
                              ),
                              name: extractAudioNameFromUrl(values.mediaUrl)
                            }
                          : null,
                        coordinates: movedCoords || {
                          x: left,
                          y: top
                        }
                      };

                      const markerNumber =
                        values.iconType === 'number' && values.iconValue != null
                          ? Number(values.iconValue)
                          : typeof values.title === 'number'
                            ? values.title
                            : audioPointNumber;

                      // Hide the AudioPoint if it's currently being edited or moved (show loader instead)
                      if (
                        (isEditingAudioPoint &&
                          editingPosition &&
                          Math.abs(
                            audioPointData.coordinates.x - editingPosition.x
                          ) < 0.1 &&
                          Math.abs(
                            audioPointData.coordinates.y - editingPosition.y
                          ) < 0.1) ||
                        (isMovingAudioPoint &&
                          movingPosition &&
                          Math.abs(
                            audioPointData.coordinates.x - movingPosition.x
                          ) < 0.1 &&
                          Math.abs(
                            audioPointData.coordinates.y - movingPosition.y
                          ) < 0.1)
                      ) {
                        return null; // Don't render this AudioPoint while editing or moving
                      }

                      return (
                        <AudioPoint
                          key={audioPointData.id}
                          data={audioPointData}
                          number={markerNumber}
                          onEdit={handleEditAudioPoint}
                          onMove={handleMoveAudioPoint}
                          onDelete={handleDeleteAudioPoint}
                        />
                      );
                    }
                    return null;
                  }
                );
              })()}

            {contentEditable &&
              tempAudioPoints.map(tempPoint => {
                const tempValues = tempPoint.values as {
                  title?: string;
                  body?: string;
                  mediaUrl?: string;
                  name?: string;
                  iconType?: string | null;
                  iconValue?: number | null;
                };
                const tempAudioPointData: AudioPointData = {
                  id: tempPoint.id.toString(),
                  text:
                    tempValues.title ??
                    tempValues.name ??
                    tempValues.body ??
                    '',
                  body: tempValues.body || '',
                  iconType: tempValues.iconType ?? null,
                  iconValue: tempValues.iconValue ?? null,
                  audio: tempValues.mediaUrl
                    ? {
                        id: 0, // Temporary
                        mediaUrl: tempValues.mediaUrl,
                        originalFilename: tempValues.name
                      }
                    : null,
                  coordinates: {
                    x: tempPoint.left as number,
                    y: tempPoint.top as number
                  }
                };

                return (
                  <AudioPoint
                    key={`temp-${tempPoint.id}`}
                    data={tempAudioPointData}
                    number={999} // High number so it doesn't conflict
                    onEdit={handleEditAudioPoint}
                    onMove={handleMoveAudioPoint}
                    onDelete={handleDeleteAudioPoint}
                  />
                );
              })}

            {/* Show loader while creating audio point */}
            {isCreatingAudioPoint && creatingPosition && (
              <div
                style={{
                  position: 'absolute',
                  left: `${creatingPosition.x}%`,
                  top: `${creatingPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #9845fb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}

            {/* Show loader while editing audio point */}
            {isEditingAudioPoint && editingPosition && (
              <div
                style={{
                  position: 'absolute',
                  left: `${editingPosition.x}%`,
                  top: `${editingPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 15,
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #9845fb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}

            {/* Show loader while moving audio point */}
            {isMovingAudioPoint && movingPosition && (
              <div
                style={{
                  position: 'absolute',
                  left: `${movingPosition.x}%`,
                  top: `${movingPosition.y}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 12,
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #9845fb',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}

            {blockers.length > 0 && body && <Instructions>{body}</Instructions>}
          </x.div>
          {contentEditable && (
            <EditBg
              divElRef={divElRef}
              id={props.id}
              type="image"
              updateFileHandler={updateBackgroundImage}
              imageGenerationPlacement="full_page_background"
            />
          )}
          {contentEditable && showColorPicker && (
            <EditBg
              divElRef={divElRef}
              id={props.id}
              type="color"
              style={{ top: '120px' }}
              hsva={bgColor}
              setHsva={setBgColor}
              updateColor={updateColor}
              parentId={parentId}
            />
          )}
        </x.div>

        {/* Overlay to block interactions while creating, editing, or moving audio points */}
        {(isCreatingAudioPoint ||
          isEditingAudioPoint ||
          isMovingAudioPoint) && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 50,
              cursor: 'wait',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={e => e.preventDefault()}
            onContextMenu={e => e.preventDefault()}
          >
            <div
              style={{
                background: 'white',
                padding: '20px 30px',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                fontFamily: 'Raleway',
                fontSize: '16px',
                color: '#333'
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #9845fb',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
              {isCreatingAudioPoint
                ? 'Creating audio point...'
                : isEditingAudioPoint
                  ? 'Updating audio point...'
                  : 'Moving audio point...'}
            </div>
          </div>
        )}

        {/* Tooltip that follows mouse cursor in editor mode */}
        {mousePosition &&
          contentEditable &&
          enableAudioPoints &&
          !showAddTextAudioModal &&
          !isCreatingAudioPoint &&
          !isEditingAudioPoint &&
          !isMovingAudioPoint && (
            <div
              style={{
                position: 'fixed',
                left: mousePosition.x - 20,
                top: mousePosition.y + 40,
                width: '220px',
                backgroundColor: '#fcf8e9',
                color: 'black',
                padding: '12px',
                borderRadius: '10px',
                border: '2px solid black',
                fontSize: '14px',
                fontFamily: 'Raleway, sans-serif',
                pointerEvents: 'none',
                zIndex: 1000,
                whiteSpace: 'normal'
              }}
            >
              <svg
                width="16"
                height="11"
                viewBox="0 0 16 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  position: 'absolute',
                  top: '-10px',
                  left: '20px'
                }}
              >
                <path d="M0 8L16 8L8 0L0 8Z" fill="black" />
                <path d="M0 10.5L16 10.5L8 2.5L0 10.5Z" fill="#FCF8E9" />
              </svg>
              Select where you'd like to add text or audio on the image
            </div>
          )}

        {/* Add Text and Audio Modal */}
        <AddTextAudioModal
          isOpen={showAddTextAudioModal}
          onClose={handleAddTextAudioModalClose}
          onSubmit={handleTextAudioSubmit}
          clickPosition={clickPosition}
          editingData={
            editingAudioPoint
              ? {
                  text: editingAudioPoint.text,
                  body: editingAudioPoint.body,
                  audio: editingAudioPoint.audio,
                  iconType: editingAudioPoint.iconType,
                  iconValue: editingAudioPoint.iconValue
                }
              : undefined
          }
          title="Add text or audio point"
        />
      </Container>
    );
  }
}

registry[IMAGE_MAP2] = ImageMap2;
